<template>
  <div class="stk-barin-page">
    <!-- 顶部查询与操作栏（全面对标 waitChk 现代风格） -->
    <header class="query-header" aria-label="条码入库确认">
      <button
        type="button"
        class="back-button"
        aria-label="返回功能首页"
        :disabled="busy"
        @click="onClickLeft"
      >
        <van-icon name="arrow-left" size="20" aria-hidden="true" />
      </button>
      <form class="search-form" role="search" @submit.prevent="onSearch">
        <div class="search-input">
          <van-icon name="search" size="14" aria-hidden="true" />
          <input
            v-model="v_search"
            :disabled="busy"
            aria-label="搜索采购订单、供应商名称或入库单号"
            placeholder="采购订单 / 供应商名称 / 入库单号"
            type="search"
            enterkeyhint="search"
            @keydown.enter.prevent="onSearch"
          />
          <button
            v-if="v_search"
            type="button"
            class="clear-button"
            aria-label="清空搜索"
            @click="clearSearch"
          >
            <van-icon name="cross" size="16" />
          </button>
        </div>
        <button
          type="submit"
          class="header-action-button"
          aria-label="搜索"
          :disabled="busy"
        >
          <van-icon name="search" size="16" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="header-action-button"
          aria-label="刷新"
          :disabled="listLoading || busy"
          @click="onSearch"
        >
          <van-icon
            name="replay"
            size="16"
            :class="{ 'is-spinning': listLoading }"
            aria-hidden="true"
          />
        </button>
      </form>
    </header>

    <!-- 主列表滚动区域：全量渲染自适应滚动，杜绝分页器 -->
    <main
      id="stk-barin-list"
      ref="listContent"
      class="list-content"
      :aria-busy="listLoading"
    >
      <div v-if="listLoading && !allData.length" class="state-panel" role="status">
        <van-loading size="24px" />
        <span>正在查询待审核入库单…</span>
      </div>
      <div v-else-if="!tabData.length" class="state-panel">
        <van-icon name="description" size="40" color="#999999" />
        <p>暂无待审核入库单据</p>
      </div>
      <template v-else>
        <article
          v-for="(item, index) in tabData"
          :key="item.IDSEQ"
          class="stk-card"
          :aria-label="'入库单 ' + item.IDNO"
        >
          <!-- 卡片头部：左侧序号、单据标签与单号，右侧待审核状态 -->
          <header class="card-header">
            <div class="card-title-group">
              <span class="card-seq-badge">{{ index + 1 }}</span>
              <span class="card-tag">入库单</span>
              <h2 class="card-idno">{{ item.IDNO }}</h2>
            </div>
            <span class="status-tag is-pending">待审核</span>
          </header>

          <!-- 单据核心信息区：现代弹性网格，杜绝任何挤压与重叠 -->
          <dl class="document-info">
            <div class="full-row">
              <dt>采购单号</dt>
              <dd class="highlight-text">{{ item.PONO || '—' }}</dd>
            </div>
            <div>
              <dt>入库日期</dt>
              <dd>{{ item.ICDTTM || '—' }}</dd>
            </div>
            <div>
              <dt>入库用户</dt>
              <dd>{{ item.ADDUSER || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>供应商</dt>
              <dd>{{ item.SUPNM || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>仓库名称</dt>
              <dd>{{ item.STKNM || '—' }}</dd>
            </div>
            <!-- 入库项数同行右侧放置【展开 / 收起】按钮 -->
            <div class="full-row count-and-expand-row">
              <div class="count-item-wrap">
                <dt>入库项数</dt>
                <dd class="count-tag-wrap">
                  <span class="count-badge">{{ item.ROWCNT || 0 }} 项</span>
                </dd>
              </div>
              <button
                type="button"
                class="toggle-expand-btn"
                :aria-expanded="isExpanded(item)"
                @click="toggleExpand(item)"
              >
                <span>{{ isExpanded(item) ? '收起' : '展开' }}</span>
                <van-icon :name="isExpanded(item) ? 'arrow-up' : 'arrow-down'" />
              </button>
            </div>
          </dl>

          <!-- 就地展开 (Inline Expand) 的入库物料明细清单 -->
          <section
            v-if="isExpanded(item)"
            class="expanded-details"
            :aria-label="'入库单 ' + item.IDNO + ' 物料明细'"
          >
            <div class="details-header">
              <span class="details-title">入库物料明细</span>
              <span class="details-badge">共 {{ (detailsMap[String(item.IDSEQ)] || []).length }} 项</span>
            </div>

            <!-- 明细异步加载状态 -->
            <div v-if="dtlLoadingMap[String(item.IDSEQ)]" class="state-panel-inline">
              <van-loading size="20px">正在读取入库明细…</van-loading>
            </div>

            <!-- 空明细提示 -->
            <div
              v-else-if="!(detailsMap[String(item.IDSEQ)] || []).length"
              class="empty-dtl-notice"
            >
              <van-icon name="info-o" color="#999999" size="18" />
              <span>暂无入库物料明细记录</span>
            </div>

            <!-- 物料明细紧凑子卡片列表 -->
            <div v-else class="dtl-card-list">
              <article
                v-for="(row, rowIdx) in detailsMap[String(item.IDSEQ)]"
                :key="(row.IDSEQ || item.IDSEQ) + '_' + (row.LSTSEQ || rowIdx)"
                class="dtl-item-card"
              >
                <!-- 物料行头部 -->
                <header class="dtl-heading">
                  <span class="dtl-seq-badge">{{ rowIdx + 1 }}</span>
                  <div class="dtl-mat-info">
                    <div class="dtl-mat-title">
                      <span class="dtl-mat-no">{{ row.MATNO || '—' }}</span>
                      <span class="dtl-mat-nm">{{ row.MATNM || '未命名物料' }}</span>
                    </div>
                    <p class="dtl-mat-spec">{{ row.MATDESC || '暂无规格描述' }}</p>
                  </div>
                </header>

                <!-- 数据网格：清晰呈现关键字段，严密对齐且自适应换行 -->
                <div class="dtl-meta-grid">
                  <div class="meta-item">
                    <span class="meta-label">送货数量</span>
                    <span class="meta-value">{{ row.SENTQTY == null || row.SENTQTY === '' ? '—' : row.SENTQTY }}</span>
                  </div>
                  <div class="meta-item highlight-item">
                    <span class="meta-label">实收数量</span>
                    <span class="meta-value highlight-val">{{ row.ACTQTY == null || row.ACTQTY === '' ? (row.STKQTY || 0) : row.ACTQTY }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">采购数量</span>
                    <span class="meta-value">{{ (row.POQTY == null || row.POQTY === '') ? '—' : row.POQTY }}{{ row.POUNIT || row.UNITNM || '' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">质检合格</span>
                    <span class="meta-value">{{ row.STKQTY == null || row.STKQTY === '' ? '0' : row.STKQTY }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓位名称</span>
                    <span class="meta-value">{{ row.PLCNM || '—' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓库名称</span>
                    <span class="meta-value">{{ row.STKNM || item.STKNM || '—' }}</span>
                  </div>
                </div>

                <!-- 物料图片区域（对标送货单及来料质检风格） -->
                <p v-if="materialImageLoading[materialImageKey(row)]" class="material-image-status" role="status">正在读取物料图片…</p>
                <div v-else-if="materialImageErrors[materialImageKey(row)]" class="material-image-status" role="alert">
                  <span>物料图片读取失败</span><button type="button" @click="loadMaterialImages([row])">重新读取</button>
                </div>
                <div v-if="materialImages(row).length" class="material-images" role="group" :aria-label="(row.MATNO || '物料') + '图片'">
                  <button
                    v-for="(src, imageIndex) in materialImages(row)"
                    :key="src"
                    type="button"
                    class="material-thumbnail"
                    :aria-label="'查看' + (row.MATNO || '物料') + '第' + (imageIndex + 1) + '张图片'"
                    @click="previewMaterialImages(row, imageIndex, $event)"
                  >
                    <van-image :src="src" fit="contain" :alt="(row.MATNM || row.MATNO || '物料') + '图片 ' + (imageIndex + 1)">
                      <template slot="error"><span class="material-image-error">加载失败</span></template>
                    </van-image>
                  </button>
                </div>
              </article>
            </div>
          </section>

          <!-- 卡片底部操作按钮：胶囊样式【拒绝】与【通过】（对标实色圆角胶囊风格） -->
          <footer class="card-footer-actions">
            <van-button
              type="danger"
              size="small"
              class="footer-action-btn reject-btn"
              :disabled="cfmBool || busy"
              @click="stfgRec(item, 'X')"
            >
              拒绝
            </van-button>
            <van-button
              type="primary"
              size="small"
              class="footer-action-btn approve-btn"
              :disabled="cfmBool || busy"
              @click="stfgRec(item, 'Y')"
            >
              通过
            </van-button>
          </footer>
        </article>
      </template>
    </main>

    <!-- 返回顶部浮动按钮 -->
    <div v-show="topShow" class="go_top_btn" @click="goTop">
      <van-icon name="arrow-up" size="24px" color="#235EFF" />
    </div>

    <!-- 物料图片大图预览（对标 stk_check.vue 与 po_cfm.vue） -->
    <van-image-preview
      v-model="imagePreviewOpen"
      :images="previewImages"
      :start-position="previewImageIndex"
      :close-on-popstate="false"
      class-name="qa-material-preview"
      role="dialog"
      aria-label="物料图片预览"
      aria-modal="true"
      @close="closeMaterialPreview"
      @keydown.esc.native.stop.prevent="closeMaterialPreview"
    >
      <template slot="cover">
        <button
          ref="imagePreviewClose"
          type="button"
          class="material-preview-close"
          aria-label="关闭图片预览"
          @click.stop="closeMaterialPreview"
        >
          关闭
        </button>
      </template>
    </van-image-preview>
  </div>
</template>

<script>
import { getDate, getDate2 } from '@/api/tools'
import { getMaterialImageUrls, materialImagePath } from '@/api/materialImages'

export default {
  name: 'StkBarin',
  data() {
    return {
      v_title: '条码入库',
      v_search: '',
      listLoading: false,
      busy: false,

      // 用户信息与权限控制
      v_usernm: this.$cookies.get('v_usernm'),
      v_username: this.$cookies.get('v_telno'),
      v_corpid: this.$cookies.get('v_corpid'),
      cfmBool: false,
      editBool: false,
      addBool: false,
      cancelBool: false,

      // 主单列表数据（全量直接渲染，杜绝分页器与伪分页）
      allData: [],
      tabData: [],

      // 就地展开状态与明细数据容器
      expandedIds: [],
      detailsMap: {},
      dtlLoadingMap: {},

      // 物料图片相关
      materialImageSets: {},
      materialImageLoading: {},
      materialImageErrors: {},
      imagePreviewOpen: false,
      previewImages: [],
      previewImageIndex: 0,

      // 滚动与返回顶部
      topShow: false,
      scroll: 0,
    }
  },
  mounted() {
    window.addEventListener('scroll', this.windowScroll, true)
    this.initData()
    this.onSearch()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.windowScroll, true)
  },
  methods: {
    // 返回上一级功能菜单
    onClickLeft() {
      if (!this.busy) {
        this.$router.push('/features')
      }
    },

    // 清空搜索框并重新检索
    clearSearch() {
      this.v_search = ''
      this.onSearch()
    },

    // 查询主单：强制锁定未审核 stfg='N'，去除 tabs
    async onSearch() {
      if (this.busy) return
      this.listLoading = true

      let v_cond = ' and corpid=' + this.v_corpid + " and adddttm>='" + getDate2(30) + "'"
      if (this.v_search && this.v_search.trim()) {
        const v_val = this.v_search.trim().toUpperCase()
        v_cond +=
          " and (pono like '%" +
          v_val +
          "%' or idno like '%" +
          v_val +
          "%' or supnm like '%" +
          v_val +
          "%' or adduser like '%" +
          v_val +
          "%' )"
      }

      // 强制在 SQL 条件中锁定未审核条件，并按录入时间倒序排序
      v_cond = " and stfg='N' " + (v_cond ? v_cond : '') + ' order by adddttm desc'

      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_cond: v_cond,
            p_table: 'V_IC_STK_H5_POIN',
          },
        })

        if (res && res.code === '200') {
          const list = Array.isArray(res.result) ? res.result : []
          this.allData = list
          this.tabData = list // 直接全量渲染，移除客户端分页切割
          // 默认将所有入库单明细全部展开并异步加载
          this.expandedIds = list.map(item => String(item.IDSEQ))
          list.forEach(item => {
            if (!this.detailsMap[String(item.IDSEQ)]) {
              this.getDtlList(item.IDSEQ)
            }
          })
        } else {
          this.$dialog.alert({
            title: '提示',
            message: (res && res.result) || '查询数据失败',
            theme: 'round-button',
          })
        }
      } catch (err) {
        this.$toast.fail('查询失败：' + (err.message || '网络异常'))
      } finally {
        this.listLoading = false
      }
    },

    // 判断单据卡片当前是否处于展开状态
    isExpanded(item) {
      return this.expandedIds.includes(String(item.IDSEQ))
    },

    // 切换卡片就地展开/收起
    toggleExpand(item) {
      const id = String(item.IDSEQ)
      const idx = this.expandedIds.indexOf(id)
      if (idx >= 0) {
        this.expandedIds.splice(idx, 1)
      } else {
        this.expandedIds.push(id)
        if (!this.detailsMap[id] || !this.detailsMap[id].length) {
          this.getDtlList(item.IDSEQ)
        }
      }
    },

    // 异步读取指定入库单的物料明细（V_IC_STKDTL_MIN）
    materialImageKey(row) {
      if (row.MATNO) return 'mat:' + row.MATNO
      const id = row.MATIDSEQ || row.MAT_IDSEQ
      return id ? 'id:' + id : 'line:' + (row.IDSEQ + '_' + row.LSTSEQ)
    },

    materialImages(row) {
      const images = this.materialImageSets[this.materialImageKey(row)]
      if (images) return images
      const mainImage = materialImagePath(row.IMGPATH)
      return mainImage ? [mainImage] : []
    },

    async loadMaterialImages(rows) {
      if (!Array.isArray(rows) || !rows.length) return
      const unique = new Map(rows.filter(r => r.MATNO || r.MATIDSEQ || r.MAT_IDSEQ).map(row => [this.materialImageKey(row), row]))
      await Promise.all(Array.from(unique, async ([key, row]) => {
        if (this.materialImageLoading[key] || this.materialImageSets[key]) return
        this.$set(this.materialImageLoading, key, true)
        this.$set(this.materialImageErrors, key, '')
        try {
          const images = await getMaterialImageUrls(row, this.v_corpid, async (data, url) => {
            const res = await this.$axios({
              method: 'post',
              url: url || 'xuper_auth/getBaseData',
              data,
            })
            return (res && res.result) || []
          })
          this.$set(this.materialImageSets, key, images)
        } catch (error) {
          this.$set(this.materialImageErrors, key, error.message || '读取失败')
        } finally {
          this.$set(this.materialImageLoading, key, false)
        }
      }))
    },

    previewMaterialImages(row, index, event) {
      const images = this.materialImages(row)
      if (!Number.isInteger(index) || index < 0 || index >= images.length) return
      this.previewImages = images
      this.previewImageIndex = index
      this._materialPreviewTrigger = event && event.currentTarget
      this.imagePreviewOpen = true
      this.$nextTick(() => {
        if (this.$refs.imagePreviewClose) this.$refs.imagePreviewClose.focus()
      })
    },

    closeMaterialPreview() {
      this.imagePreviewOpen = false
      const trigger = this._materialPreviewTrigger
      this._materialPreviewTrigger = null
      if (trigger && trigger.isConnected) this.$nextTick(() => trigger.focus())
    },

    async getDtlList(v_idseq) {
      const id = String(v_idseq)
      this.$set(this.dtlLoadingMap, id, true)

      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: v_idseq,
            p_table: 'V_IC_STKDTL_MIN',
            p_cond: ' order by MATNO',
          },
        })

        if (res && res.code === '200' && Array.isArray(res.result)) {
          this.$set(this.detailsMap, id, res.result)
          this.loadMaterialImages(res.result)
        } else {
          this.$dialog.alert({
            title: '提示',
            message: (res && res.result) || '获取物料明细失败',
            theme: 'round-button',
          })
        }
      } catch (err) {
        this.$toast.fail('明细加载失败：' + (err.message || '网络异常'))
      } finally {
        this.$set(this.dtlLoadingMap, id, false)
      }
    },

    // 单据状态处理：通过 ('Y') / 拒绝 ('X')
    stfgRec(item, val) {
      if (this.busy) return
      const actionText = val === 'Y' ? '通过' : '拒绝'

      this.$dialog
        .confirm({
          title: '提示',
          message: '你确定要' + actionText + '此入库单据吗？',
          confirmButtonColor: val === 'Y' ? '#235EFF' : '#ee0a24',
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        })
        .then(async () => {
          let v_cond = ''
          if (val === 'Y') {
            const v_chkdttm = getDate('full')
            const v_chkuser = this.v_usernm || ''
            v_cond = " set stfg='Y',chkuser='" + v_chkuser + "',chkdttm='" + v_chkdttm + "'"
          } else {
            v_cond = " set stfg='X',chkuser='',chkdttm=''"
          }

          const loadingToast = this.$toast.loading({
            message: actionText + '处理中...',
            forbidClick: true,
            duration: 0,
            loadingType: 'spinner',
          })

          try {
            const res = await this.$axios({
              method: 'post',
              url: 'xuper_auth/updateRec',
              data: {
                p_key: 'idseq',
                p_value: item.IDSEQ,
                p_table: 'ic_stk',
                p_cond: v_cond,
              },
            })

            if (res && String(res.code) === '200') {
              this.$toast.success(actionText + '成功')
              // 移除展开状态
              const expIdx = this.expandedIds.indexOf(String(item.IDSEQ))
              if (expIdx >= 0) this.expandedIds.splice(expIdx, 1)
              await this.onSearch()
            } else {
              this.$toast({
                type: 'html',
                icon: 'warning-o',
                message: (res && res.result) || actionText + '失败',
                size: 'large',
                duration: 3000,
              })
            }
          } catch (err) {
            this.$dialog.alert({
              title: '操作失败',
              message: err.message || actionText + '出现异常，请重试',
            })
          } finally {
            loadingToast.clear()
          }
        })
        .catch(() => {})
    },

    // 页面初始化与用户权限
    initData() {
      // 获取功能模块配置标题
      if (this.$route && this.$route.query && this.$route.query.path) {
        this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'isqry',
            p_value: 'M',
            p_key2: 'frmurl',
            p_value2: this.$route.query.path,
            p_table: 'V_Sys_form',
          },
        }).then(res => {
          if (res && res.result && res.result.length > 0) {
            this.v_title = res.result[0].CFRMNM || '条码入库'
          }
        })
      }

      // 获取当前用户权限
      this.$axios({
        method: 'post',
        url: 'xuper_auth/getBaseData',
        data: {
          p_key: 'userno',
          p_value: this.v_username,
          p_key2: 'frmidseq',
          p_value2: '419829',
          p_table: 'v_sys_user_priv',
        },
      }).then(res => {
        if (res && res.result && res.result.length > 0) {
          const row = res.result[0]
          this.addBool = row.ISADD === 'N'
          this.editBool = row.ISUPD === 'N'
          this.cfmBool = row.ISCFM === 'N'
          this.cancelBool = row.ISCANCEL === 'N'
        }
      })
    },

    // 监听滚动位置以显隐返回顶部按钮
    windowScroll() {
      const container = this.$refs.listContent
      const scrollPos = container
        ? container.scrollTop
        : document.documentElement.scrollTop || document.body.scrollTop
      this.topShow = scrollPos > 240
    },

    // 平滑返回顶部
    goTop() {
      const container = this.$refs.listContent
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/scss/variables.scss';

.stk-barin-page {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f7f8fa;
  color: $text-primary;
  font: 14px/1.45 $font-family-base;
  overflow: hidden;
  box-sizing: border-box;
}

// 头部顶栏（对标 waitChk.vue 现代风格）
.query-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 48px;
  padding: 4px 8px;
  background: #ffffff;
  border-bottom: 1px solid #ebedf0;
  z-index: 10;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-primary;

  &:disabled {
    color: $text-tertiary;
  }
}

.search-form {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
}

.search-input {
  min-width: 0;
  flex: 1;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid $border-color;
  border-radius: 16px;
  background: #f7f8fa;
  color: $text-secondary;

  &:focus-within {
    border-color: $primary-color;
    background: #ffffff;
  }

  input {
    width: 0;
    min-width: 0;
    flex: 1;
    height: 30px;
    outline: none;
    border: none;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: $text-primary;
  }

  input::-webkit-search-cancel-button {
    display: none;
  }
}

.clear-button {
  width: 28px;
  height: 28px;
  flex: none;
  border: 0;
  background: transparent;
  color: $text-secondary;
}

.header-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: none;
  padding: 0;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  background: #ffffff;
  color: $text-primary;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &:active {
    background: #f0f2f5;
  }

  &:disabled {
    color: $text-tertiary;
    box-shadow: none;
  }
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

// 主列表滚动区域
.list-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px;
}

.state-panel {
  min-height: 180px;
  padding: 28px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  color: $text-secondary;
}

// 主单卡片样式（对标送货审核 po_cfm 沉浸卡片与加深边框）
.stk-card {
  max-width: 760px;
  margin: 0 auto 10px;
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.card-seq-badge {
  width: 18px;
  height: 18px;
  line-height: 18px;
  flex: none;
  border-radius: 2px;
  text-align: center;
  background: #edf2ff;
  color: $primary-color;
  font-size: 11px;
  font-weight: 600;
}

.card-tag {
  flex: none;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
  background: #edf2ff;
  color: $primary-color;
  font-weight: 500;
}

.card-idno {
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.status-tag {
  flex: none;
  border-radius: 3px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 500;
}

.is-pending {
  background: #fff0ed;
  color: #ff4000;
  border: 1px solid #ffd6cc;
}

// 展开/收起按钮（微胶囊按钮）
.toggle-expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #f8f9fa;
  color: $primary-color;
  font-size: 11px;
  font-weight: 500;
  flex: none;

  &:active {
    background: #ebedf0;
  }
}

// 单据核心信息区（完全杜绝硬编码固定宽度，自适应弹性双列）
.document-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 3px 8px;
  margin: 0;
  font-size: 12px;
  line-height: 19px;

  > div {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: baseline;
    gap: 4px;
    min-width: 0;
  }

  dt {
    font-size: 11.5px;
    color: $text-secondary;
    white-space: nowrap;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
    word-break: break-all;
    color: $text-primary;
    font-size: 12px;
  }
}

.full-row {
  grid-column: 1 / -1;
}

.count-and-expand-row {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 2px;
}

.count-item-wrap {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.highlight-text {
  font-weight: 600;
  color: #103b9a;
}

.count-tag-wrap {
  display: flex;
  align-items: center;
}

.count-badge {
  display: inline-block;
  padding: 0 5px;
  border-radius: 3px;
  background: #f0f5ff;
  color: $primary-color;
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
}

// 就地展开容器（紧凑排版）
.expanded-details {
  margin-top: 8px;
  padding: 8px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
}

.details-title {
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
}

.details-badge {
  font-size: 11px;
  color: $text-secondary;
}

.state-panel-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  color: $text-secondary;
  font-size: 12px;
}

.empty-dtl-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 6px;
  background: #f8fafc;
  border-radius: 4px;
  color: $text-secondary;
  font-size: 11.5px;
}

// 物料明细子卡片列表
.dtl-card-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dtl-item-card {
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  background: white;
}

.dtl-heading {
  display: flex;
  gap: 5px;
  align-items: flex-start;
}

.dtl-seq-badge {
  width: 16px;
  height: 16px;
  line-height: 16px;
  flex: none;
  border-radius: 2px;
  text-align: center;
  background: #edf2ff;
  color: $primary-color;
  font-size: 10px;
  font-weight: 600;
  margin-top: 1px;
}

.dtl-mat-info {
  flex: 1;
  min-width: 0;
}

.dtl-mat-title {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 5px;
  align-items: baseline;
}

.dtl-mat-no {
  font-size: 12px;
  font-weight: 600;
  color: #1a202c;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-nm {
  font-size: 12px;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-spec {
  margin: 1px 0 0;
  font-size: 11px;
  color: $text-secondary;
  line-height: 15px;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 明细数据网格
.dtl-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin-top: 4px;
  padding: 4px 6px;
  background: #ffffff;
  border-radius: 4px;
  border: 1px solid #edf2f7;
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  line-height: 18px;
  min-width: 0;
}

.meta-label {
  color: $text-secondary;
  white-space: nowrap;
}

.meta-value {
  font-weight: 500;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
  font-variant-numeric: tabular-nums;
}

.highlight-val {
  font-weight: 700;
  color: #235eff;
}

// 卡片底部操作按钮区域（居右对齐的圆角实色胶囊按钮）
.card-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.footer-action-btn {
  flex: none;
  min-width: 66px;
  height: 26px;
  line-height: 24px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 13px;
  color: #ffffff !important;
  border: none !important;
}

.reject-btn {
  background-color: #ff4d4f !important;

  &:active {
    background-color: #d9363e !important;
  }
}

.approve-btn {
  background-color: #1ecd84 !important;

  &:active {
    background-color: #12a66a !important;
  }
}

// 物料缩略图及预览样式（对标 stk_check.vue 与 po_cfm.vue）
.material-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, 48px);
  gap: 6px;
  margin-top: 6px;
}

.material-image-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 0;
  font-size: 11px;
  color: $text-secondary;

  button {
    min-width: 48px;
    height: 26px;
    padding: 0 4px;
    border: 0;
    background: transparent;
    color: $primary-color;
    font-size: 11px;
  }
}

.material-thumbnail {
  position: relative;
  display: block;
  min-width: 0;
  width: 100%;
  padding: 0;
  border: 1px solid #ebedf0;
  border-radius: 4px;
  overflow: hidden;
  background: #f7f8fa;

  &::before {
    display: block;
    padding-top: 100%;
    content: '';
  }

  ::v-deep .van-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.material-image-error {
  font-size: 10px;
  color: $text-tertiary;
}

.material-preview-close {
  position: fixed;
  top: calc(12px + env(safe-area-inset-top));
  right: 12px;
  z-index: 1;
  min-width: 48px;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 14px;
}

// 返回顶部浮动按钮
.go_top_btn {
  position: fixed;
  right: 16px;
  bottom: 24px;
  z-index: 99;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 50%;
  border: 1px solid #dcdfe6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  cursor: pointer;

  &:active {
    background: #f7f8fa;
  }
}
</style>
